import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DataTable from "../../common/dataTable";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useCapaAccess } from "./capaAccess";
import { fetchRemovedCapaList } from "./capaService";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import HoverPreview from "../../common/HoverPreview/HoverPreview";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";

const CapaRemovedList = () => {
  const navigate = useNavigate();
  const { userData, canViewRemovedCapa } = useCapaAccess();
  const [rows, setRows] = useState([]);
  const [staffById, setStaffById] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchRemovedCapaList(userData);
      if (!cancelled) setRows(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) return;
      const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
        OrganizationId: userData.OrganizationId,
        StaffId: userData.StaffId,
        Action: "Get",
        AccessCode: "",
        Password: "",
        HaveProfileViewRights: 1,
        HaveProfileEditRights: 0,
        HaveDelegationLogViewRights: 1,
        HaveDelegationLogEditRights: 0,
        HavingLineManagerRights: 1,
        HaveStudyTaskArchiveRights: 0,
        CreatedBy: userData.StaffId,
      });
      if (cancelled || response?.success !== 1) return;
      const users = Array.isArray(response?.data?.UsersActivity)
        ? response.data.UsersActivity
        : [];
      const map = {};
      users.forEach((u) => {
        if (!u?.StaffId) return;
        map[String(u.StaffId).toLowerCase()] =
          u.Name || u.EmailId || String(u.StaffId);
      });
      setStaffById(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const resolveRemovedByName = useMemo(() => {
    const myName =
      [userData?.FirstName, userData?.LastName].filter(Boolean).join(" ").trim() ||
      userData?.Name ||
      userData?.EmailId ||
      "";
    return (inactiveBy) => {
      const raw = String(inactiveBy || "").trim();
      if (!raw) return "—";
      const key = raw.toLowerCase();
      if (staffById[key]) return staffById[key];
      if (userData?.StaffId && key === String(userData.StaffId).toLowerCase()) {
        return myName || raw;
      }
      return raw;
    };
  }, [staffById, userData]);

  const columns = useMemo(
    () => [
      { label: "CAPA ID", accessor: "CapaNo" },
      {
        label: "Title",
        accessor: "Title",
        render: (row) => {
          const raw = String(row.Title || "");
          if (!raw.trim()) {
            return <span className="text-muted">—</span>;
          }
          return (
            <HoverPreview
              content={raw}
              plainTextForPreview={raw}
              isHtml={false}
              previewLength={48}
              appendEllipsis={true}
            />
          );
        },
      },
      { label: "Status", accessor: "Status" },
      { label: "Owner", accessor: "OwnerName" },
      {
        label: "Removed by",
        accessor: "InactiveBy",
        align: "center",
        render: (row) => {
          const name = resolveRemovedByName(row.InactiveBy);
          return (
            <div
              title={name}
              style={{
                width: "1.4rem",
                height: "1.4rem",
                minWidth: "1.4rem",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1e293b",
                fontSize: "0.6rem",
                fontWeight: 600,
                lineHeight: 1,
                backgroundColor: getAvatarColor(name),
              }}
            >
              {getInitials(name)}
            </div>
          );
        },
      },
      { label: "Removed at", accessor: "InactiveDateTime", type: "date" },
      {
        label: "Reason",
        accessor: "InactiveReason",
        render: (row) => {
          const reason = String(row.InactiveReason || "").trim();
          if (!reason) return <span className="text-muted">—</span>;
          return (
            <HoverPreview
              content={reason}
              plainTextForPreview={reason}
              isHtml={false}
              previewLength={42}
              appendEllipsis={true}
            />
          );
        },
      },
    ],
    [resolveRemovedByName],
  );

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canViewRemovedCapa) return <Navigate to="/notification-center" replace />;

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA", path: "/capa" },
          { label: "Removed list" },
        ]}
        actions={[
          { icon: "fas fa-chart-pie", label: "Dashboard", onClick: () => navigate("/capa-dashboard") },
          { icon: "fas fa-plus", label: "Create CAPA", onClick: () => navigate("/capa/new") },
          { icon: "fas fa-list", label: "CAPA list", onClick: () => navigate("/capa") },
          { icon: "fas fa-trash-alt", label: "Removed CAPA list", onClick: () => navigate("/capa/removed") },
        ]}
      />
      <DataTable
        data={rows}
        columns={columns}
        sortable={["CapaNo", "Title", "OwnerName", "InactiveDateTime"]}
        showToggle={true}
      />
    </div>
  );
};

export default CapaRemovedList;
